/** Presentation helpers shared by every screen. */

const dateTime = new Intl.DateTimeFormat('pl-PL', { dateStyle: 'short', timeStyle: 'medium' });
const dateOnly = new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium' });
const number = new Intl.NumberFormat('pl-PL');

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateTime.format(date);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateOnly.format(date);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return number.format(value);
}

/** Cost is never invented: an unpriced record shows as "nieznany". */
export function formatCost(value: number | string | null | undefined, status?: string): string {
  if (status === 'UNKNOWN' || value === null || value === undefined) return 'nieznany';
  const amount = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(amount)) return 'nieznany';
  return `${amount.toFixed(amount < 0.01 ? 6 : 2)} USD${status === 'PARTIAL' ? ' (częściowy)' : ''}`;
}

export function formatMs(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value < 1_000) return `${value} ms`;
  return `${(value / 1_000).toFixed(2)} s`;
}

export function relativeTime(value: string | Date | null | undefined): string {
  if (!value) return 'nigdy';
  const date = typeof value === 'string' ? new Date(value) : value;
  const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ];
  const formatter = new Intl.RelativeTimeFormat('pl-PL', { numeric: 'auto' });
  let value2 = deltaSeconds;
  for (const [unit, size] of units) {
    if (Math.abs(value2) < size) return formatter.format(Math.round(value2), unit);
    value2 /= size;
  }
  return formatter.format(Math.round(value2), 'year');
}

export function toneForHealth(state: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (state) {
    case 'HEALTHY':
      return 'ok';
    case 'DEGRADED':
      return 'warn';
    case 'UNHEALTHY':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function toneForRequestStatus(status: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (status) {
    case 'SUCCESS':
      return 'ok';
    case 'ERROR':
      return 'danger';
    case 'CANCELLED':
      return 'warn';
    default:
      return 'neutral';
  }
}

export function toneForSideEffect(sideEffect: string): 'ok' | 'warn' | 'danger' | 'info' {
  switch (sideEffect) {
    case 'READ':
      return 'ok';
    case 'WRITE_ADDITIVE':
    case 'WRITE_MUTATING':
      return 'warn';
    case 'DESTRUCTIVE':
    case 'FINANCIAL_OR_LEGAL_EFFECT':
    case 'EXTERNAL_COMMUNICATION':
      return 'danger';
    default:
      return 'info';
  }
}

export function toneForLifecycle(state: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (state) {
    case 'PUBLISHED':
      return 'ok';
    case 'DRAFT':
      return 'neutral';
    case 'DEPRECATED':
      return 'warn';
    case 'RETIRED':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function prettyJson(value: unknown): string {
  return JSON.stringify(value ?? null, null, 2);
}
